import React from "react";
import { useTinyHost } from "@tiny/host-contract";
import { SegmentDetailsView } from "@tiny/api";
import { Button, Box, Tooltip } from "@tiny/components";
import PageHeader from "@tiny/components/src/PageHeader";
import CleanedListHistoryDialog from "../../../components/CleanedListHistoryDialog";
import { useAudienceSourceDetailsHeader } from "./AudienceSourceDetailsHeader.hooks";
import CleaningService from "@tiny/icons/generated/CleaningService";
import Download from "@tiny/icons/generated/Download";

const CLEAN_LIST_PRODUCT_IDS = {
  STAGE: "ff2d8ca0-8019-4a80-b317-3428ab04553b",
  PROD: "6df2e7bd-7687-4221-ac62-acf85f45e39b",
};

export interface AudienceSourceDetailsHeaderProps {
  title: string;
  segmentId: string;
  segmentType: SegmentDetailsView["segmentType"];
  onImport: () => void;
  importDisabled?: boolean;
  enableEdit?: boolean;
  cleaned?: boolean;
  status?: string;
}

function AudienceSourceDetailsHeader(props: AudienceSourceDetailsHeaderProps) {
  const cleanListProductId = useTinyHost().capabilities.isStagingEnvironment
    ? CLEAN_LIST_PRODUCT_IDS.STAGE
    : CLEAN_LIST_PRODUCT_IDS.PROD;
  const { capabilities, slots } = useTinyHost();
  const { isShopifyPlan } = capabilities;
  const { navigate } = useTinyHost();
  const {
    onImport,
    importDisabled,
    enableEdit = true,
    cleaned,
    status,
    segmentType,
    segmentId,
  } = props;

  const {
    editTitle,
    editing,
    saveTitle,
    saving,
    cancelEdit,
    mdUp,
    internalTitle,
    titleEditable,
    onCleanList,
    onDownloadCleanedList,
    cleanedHistoryDialogOpen,
    closeCleanedHistoryDialog,
    productDetailsDialogOpen,
    closeProductDetailsDialog,
  } = useAudienceSourceDetailsHeader(props);

  return (
    <PageHeader
      mb={mdUp ? 6 : 0}
      backUrl="/main/audience?tab=sources"
      onTitleCancel={cancelEdit}
      onTitleEdit={editTitle}
      onTitleSave={saveTitle}
      title={internalTitle || "-"}
      titleEditLabel="Audience name"
      titleEditable={titleEditable && enableEdit}
      titleEditing={editing}
      titleMaxLength={140}
      titleSaving={saving}
      chip={null}
      actions={
        mdUp && (
          <>
            {status === "VERIFICATION" ? (
              <Box display="flex" alignItems="center" height="100%" mr={2}>
                <Tooltip title="List cleaning in progress">
                  <CleaningService
                    style={{ width: 20, height: 20, color: "#9c27b0" }}
                  />
                </Tooltip>
              </Box>
            ) : (
              <>
                {capabilities.cleanListVisible !== false &&
                  !isShopifyPlan &&
                  segmentType === "UPLOAD" &&
                  (status === "COMPLETED" || cleaned) && (
                    <Box mr={2}>
                      <Button
                        variant="contained"
                        onClick={onCleanList}
                        style={{ backgroundColor: "#9c27b0", color: "#fff" }}
                        endIcon={<CleaningService />}
                      >
                        Clean My List
                      </Button>
                    </Box>
                  )}

                {capabilities.cleanListVisible !== false &&
                  !isShopifyPlan &&
                  segmentType === "UPLOAD" &&
                  cleaned && (
                    <Box mr={2}>
                      <Button
                        variant="contained"
                        onClick={onDownloadCleanedList}
                        style={{ backgroundColor: "#5934ffff", color: "#fff" }}
                        endIcon={<Download />}
                      >
                        Download Cleaned List
                      </Button>
                    </Box>
                  )}
              </>
            )}

            <Button
              variant="contained"
              onClick={onImport}
              disabled={importDisabled}
            >
              Import
            </Button>

            <CleanedListHistoryDialog
              open={cleanedHistoryDialogOpen}
              onClose={closeCleanedHistoryDialog}
              segmentId={segmentId}
            />

            {slots?.productDetailsDialog?.({
              open: productDetailsDialogOpen,
              onClose: closeProductDetailsDialog,
              productId: cleanListProductId,
              segmentId: segmentId,
              onSuccess: () => navigate.to("/main/audience"),
            })}
          </>
        )
      }
    />
  );
}

export default React.memo(AudienceSourceDetailsHeader);
